import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import InteractiveBlock from '../components/study/InteractiveBlock';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { studentAPI } from '../utils/api';

const StudyPage = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { error } = useToast();
  const chatEndRef = useRef(null);

  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layer, setLayer] = useState(1);
  const [visual, setVisual] = useState(null);
  const [visualLoading, setVisualLoading] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const sessionIdRef = useRef(null);
  const sessionStartRef = useRef(null);
  const [language, setLanguage] = useState('en');

  // {t('study.askGuruji')} state
  const [gurujiMessages, setGurujiMessages] = useState([
    { role: 'guruji', text: "Namaste! I'm here to help with anything about this topic. What's your doubt? 🙏" }
  ]);
  const [gurujiInput, setGurujiInput] = useState('');
  const [gurujiLoading, setGurujiLoading] = useState(false);
  const [gurujiOpen, setGurujiOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportForm, setReportForm] = useState({ issue_type: 'incorrect_content', description: '' });
  const [reportSent, setReportSent] = useState(false);

  useEffect(() => { loadTopic(); }, [topicId]);

  useEffect(() => {
    return () => {
      // End session when leaving page
      if (sessionIdRef.current && sessionStartRef.current) {
        const duration = Math.round((Date.now() - sessionStartRef.current) / 1000);
        studentAPI.post('/student/sessions/end', {
          session_id: sessionIdRef.current,
          duration_seconds: duration,
          layer_reached: layer,
          asked_guruji: gurujiMessages.length > 1
        }).catch(() => {});
      }
    };
  }, []);

  const getContent = (t, l) => {
    const base = l === 1 ? t.layer_1 : l === 2 ? t.layer_2 : t.layer_3;
    const tr = t.translations && t.translations[language];
    if (language === 'en' || !tr) return base;
    if (l === 1) return { ...base, bullets: tr.bullets || base.bullets, example: tr.example || base.example, paragraph: tr.paragraph || base.paragraph };
    if (l === 2) return { ...base, explanation: tr.layer_2_explanation || base.explanation, examples: tr.layer_2_examples || base.examples };
    if (l === 3) return { ...base, simple_breakdown: tr.layer_3_simple_breakdown || base.simple_breakdown, analogy: tr.layer_3_analogy || base.analogy };
    return base;
  };

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [gurujiMessages]);

  const loadTopic = async () => {
    setLoading(true);
    try {
      const res = await studentAPI.getTopic(topicId);
      setTopic(res.data.data);
      // Load visual
      setVisualLoading(true);
      try {
        const vRes = await studentAPI.getTopicVisual(topicId);
        if (vRes.data.data?.svg) {
        let svg = vRes.data.data.svg;
        // Extract only the SVG tag
        const svgMatch = svg.match(/<svg[\s\S]*<\/svg>/i);
        if (svgMatch) svg = svgMatch[0];
        // Make responsive
        svg = svg.replace(/width="[^"]*"/, 'width="100%"').replace(/height="[0-9]+"/, 'height="auto"');
        setVisual(svg);
      }
      } catch {} finally { setVisualLoading(false); }
      setLayer(1);
      setUnderstood(false);
      // Start tracking session
      try {
        const sRes = await studentAPI.post('/student/sessions/start', { topic_id: id, subject: res.data.data.subject, session_type: 'topic' });
        sessionIdRef.current = sRes.data.data?.session_id;
        sessionStartRef.current = Date.now();
      } catch {}
    } catch {
      error('Failed to load topic content.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnderstood = () => {
    setUnderstood(true);
    navigate(`/quiz/${topicId}`);
  };

  const handleReport = async () => {
    if (!reportForm.description.trim()) return error('Please describe the issue.');
    try {
      await studentAPI.reportError(topicId, reportForm);
      setReportSent(true);
      setTimeout(() => { setShowReport(false); setReportSent(false); setReportForm({ issue_type: 'incorrect_content', description: '' }); }, 2000);
      success('Report submitted. Thank you!');
    } catch { error('Failed to submit report.'); }
  };

  const handleAskGuruji = async () => {
    if (!gurujiInput.trim() || gurujiLoading) return;
    const question = gurujiInput.trim();
    setGurujiInput('');
    setGurujiMessages(prev => [...prev, { role: 'student', text: question }]);
    setGurujiLoading(true);

    try {
      const res = await studentAPI.askGuruji({ topic_id: topicId, question });
      const { answer, related_topic } = res.data.data;
      setGurujiMessages(prev => [...prev, { role: 'guruji', text: answer, related_topic }]);
    } catch {
      setGurujiMessages(prev => [...prev, {
        role: 'guruji',
        text: 'I apologise, I am having trouble right now. Please try again in a moment.'
      }]);
    } finally {
      setGurujiLoading(false);
    }
  };

  const RevisionProgress = () => {
    if (!topic?.revisions?.length) return null;
    const steps = ['Study', 'Day 1', 'Day 4', 'Day 7', 'Mastered'];
    const completedRevisions = topic.revisions.filter(r => r.status === 'completed').length;
    const currentStep = completedRevisions + 1;

    return (
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--muted)', marginBottom: '8px' }}>1-4-7 REVISION PROGRESS</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center', padding: '6px 2px', fontSize: '10px', fontWeight: 700,
              background: i < currentStep ? 'var(--green)' : i === currentStep ? 'var(--brand-primary)' : 'var(--bg)',
              color: i <= currentStep ? 'white' : 'var(--muted)',
              border: i > currentStep ? '1px solid var(--border)' : 'none',
              borderRadius: i === 0 ? 'var(--radius-sm) 0 0 var(--radius-sm)' : i === steps.length - 1 ? '0 var(--radius-sm) var(--radius-sm) 0' : 0
            }}>
              {s}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AppShell>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
        </div>
      </AppShell>
    );
  }

  if (!topic) {
    return (
      <AppShell>
        <div className="page-content empty-state">
          <div className="empty-state-icon">📚</div>
          <div className="empty-state-text">Topic not found</div>
          <div className="empty-state-sub">This topic content is not available yet.</div>
          <button className="btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/subjects')}>Back to Subjects</button>
        </div>
      </AppShell>
    );
  }

  const currentLayer = getContent(topic, layer);

  return (
    <AppShell>
      <div className="topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--muted)' }}>
          <span style={{ cursor: 'pointer', color: 'var(--text)', fontWeight: 700 }} onClick={() => navigate('/subjects')}>{topic.subject}</span>
          <span style={{ color: 'var(--border)' }}>›</span>
          <span style={{ cursor: 'pointer', color: 'var(--text)', fontWeight: 700 }} onClick={() => navigate(`/subjects/${topic.subject?.toLowerCase().replace(/ /g, '_')}`)}>{topic.chapter_name}</span>
          <span style={{ color: 'var(--border)' }}>›</span>
          <span style={{ color: 'var(--brand-primary)' }}>{topic.topic_name}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {topic?.translations && Object.keys(topic.translations).length > 0 && (
            <div style={{ display: 'flex', gap: '4px' }}>
              {['en', ...Object.keys(topic.translations)].map(lang => {
                const labels = { en: 'EN', hi: 'हि', mr: 'म', ta: 'த', te: 'తె', bn: 'বা' };
                return (
                  <button key={lang} onClick={() => setLanguage(lang)}
                    style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 800, border: 'none', cursor: 'pointer', background: language === lang ? 'var(--brand-primary)' : 'var(--bg)', color: language === lang ? 'white' : 'var(--muted)' }}>
                    {labels[lang] || lang.toUpperCase()}
                  </button>
                );
              })}
            </div>
          )}
          <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 600 }}>No time limit · Read at your own pace</span>
        </div>
      </div>

      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Topic header */}
          <div className="fade-up-1">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <span style={{ fontSize: '10px', fontWeight: 800, background: 'var(--brand-secondary-light)', color: 'var(--brand-secondary)', borderRadius: '20px', padding: '2px 9px' }}>{topic.subject}</span>
              <span style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600 }}>{topic.chapter_name}</span>
              <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 800, background: 'var(--brand-primary-light)', color: 'var(--brand-primary)', borderRadius: '20px', padding: '3px 10px' }}>Layer {layer} of 3</span>
            </div>
            <h1 style={{ fontSize: '22px', fontWeight: 800, marginBottom: '4px' }}>{topic.topic_name}</h1>
            <p style={{ fontSize: '12px', color: 'var(--muted)' }}>Understanding {topic.topic_name} — {topic.subject}</p>
          </div>

          {/* Content block */}
          <div className="card fade-up-2" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Key Points */}
            {currentLayer?.bullets?.length > 0 && (
              <div>
                <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>● Key Points</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {currentLayer.bullets.map((b, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '16px', lineHeight: 1.8 }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--brand-primary)', flexShrink: 0, marginTop: '6px' }}></div>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Paragraph */}
            {currentLayer?.paragraph && (
              <div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>📖 In Short</div>
                <p style={{ fontSize: '15px', lineHeight: 1.8, color: 'var(--text)' }}>{currentLayer.paragraph}</p>
              </div>
            )}

            {/* Visual placeholder */}
            {currentLayer?.visual && (
              <div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>🔬 Visual</div>
                <div style={{ background: 'var(--brand-secondary-light)', borderRadius: 'var(--radius-sm)', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px', border: '1px solid var(--border)' }}>
                  {currentLayer.visual.description
                    ? (visualLoading 
                      ? <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)', fontSize: '12px' }}>⏳ Generating diagram...</div>
                      : visual 
                        ? <div dangerouslySetInnerHTML={{ __html: visual }} style={{ width: '100%', overflowX: 'auto', maxWidth: '100%' }} />
                        : <p style={{ fontSize: '13px', color: 'var(--brand-secondary)', textAlign: 'center', fontWeight: 600 }}>{currentLayer.visual.description}</p>
                    )
                    : <p style={{ fontSize: '13px', color: 'var(--muted)', textAlign: 'center' }}>Diagram — {topic.topic_name}</p>
                  }
                </div>
              </div>
            )}

            {/* Example */}
            {currentLayer?.example && (
              <div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>💡 Real-Life Example</div>
                <div style={{ background: 'var(--brand-primary-light)', borderLeft: '3px solid var(--brand-primary)', padding: '12px 14px', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' }}>
                  <p style={{ fontSize: '15px', lineHeight: 1.8 }}>{currentLayer.example}</p>
                </div>
              </div>
            )}

            {/* Layer 2/3 extra explanation */}
            {layer > 1 && (currentLayer?.explanation || currentLayer?.simple_breakdown) && (
              <div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>📘 Deeper Explanation</div>
                <p style={{ fontSize: '16px', lineHeight: 1.9 }}>{currentLayer.explanation || currentLayer.simple_breakdown}</p>
              </div>
            )}

            
            {layer === 2 && currentLayer?.examples?.length > 0 && (
              <div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>💡 More Examples</div>
                {currentLayer.examples.map((ex, i) => (
                  <div key={i} style={{ background: 'var(--brand-primary-light)', borderLeft: '3px solid var(--brand-primary)', padding: '10px 12px', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0', marginBottom: '8px', fontSize: '15px', lineHeight: 1.8 }}>{ex}</div>
                ))}
              </div>
            )}
            {currentLayer?.interactive && (
              <div style={{ marginTop: '14px' }}>
                <InteractiveBlock data={currentLayer.interactive} />
              </div>
            )}

            {layer === 3 && currentLayer?.analogy && (
              <div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>🌱 Daily Life Analogy</div>
                <div style={{ background: 'var(--green-light)', borderLeft: '3px solid var(--green)', padding: '12px 14px', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' }}>
                  <p style={{ fontSize: '15px', lineHeight: 1.8 }}>{currentLayer.analogy}</p>
                </div>
              </div>
            )}
          </div>

          {/* Still confused? */}
          {layer < 3 && (
            <div className="fade-up-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>😕 {t('study.stillConfused')}</span>
              <button
                onClick={() => setLayer(l => Math.min(l + 1, 3))}
                style={{ background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: '20px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.target.style.borderColor = 'var(--brand-primary)'}
                onMouseLeave={e => e.target.style.borderColor = 'var(--border)'}
              >
                {t('study.showMoreExamples')} ›
              </button>
            </div>
          )}

          {layer === 3 && (
            <div style={{ padding: '10px 14px', background: 'var(--green-light)', borderRadius: 'var(--radius-sm)', fontSize: '12px', color: 'var(--green)', fontWeight: 700 }}>
              ✓ You've seen all three explanation layers. If still confused, ask Guruji on the right!
            </div>
          )}

          {/* Report Error */}
          <div style={{ textAlign: 'right', marginTop: '8px' }}>
            <button onClick={() => setShowReport(true)} style={{ background: 'none', border: 'none', fontSize: '11px', color: 'var(--muted)', cursor: 'pointer', textDecoration: 'underline' }}>
              ⚠️ Report an error in this content
            </button>
          </div>

          {/* Report Modal */}
          {showReport && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
              <div style={{ background: 'white', borderRadius: '14px', padding: '24px', width: '100%', maxWidth: '420px' }}>
                <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>⚠️ Report Content Error</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '16px' }}>Help us improve — tell us what's wrong with this content.</div>
                {reportSent ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#1B7F3E', fontWeight: 700 }}>✅ Report submitted. Thank you!</div>
                ) : (
                  <>
                    <select value={reportForm.issue_type} onChange={e => setReportForm(p => ({ ...p, issue_type: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid var(--border)', fontSize: '13px', marginBottom: '10px', outline: 'none' }}>
                      <option value="incorrect_content">Incorrect information</option>
                      <option value="outdated_content">Outdated / not in NCERT</option>
                      <option value="spelling_grammar">Spelling or grammar error</option>
                      <option value="wrong_example">Wrong example</option>
                      <option value="other">Other issue</option>
                    </select>
                    <textarea value={reportForm.description} onChange={e => setReportForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Describe the issue in detail..."
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid var(--border)', fontSize: '13px', minHeight: '80px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }} />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={handleReport} style={{ flex: 1, background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Submit Report</button>
                      <button onClick={() => setShowReport(false)} style={{ flex: 1, background: 'var(--bg)', color: 'var(--text)', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="fade-up-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <button
              onClick={handleUnderstood}
              style={{ background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: 'var(--radius-sm)', padding: '12px', fontSize: '14px', fontWeight: 800, cursor: 'pointer' }}
            >
              {`✓ ${t('study.understood')}`}
            </button>
            <button
              onClick={() => navigate(`/quiz/${topicId}`)}
              style={{ background: 'var(--brand-secondary)', color: 'white', border: 'none', borderRadius: 'var(--radius-sm)', padding: '12px', fontSize: '14px', fontWeight: 800, cursor: 'pointer' }}
            >
              📅 Revising? Go to Quiz
            </button>
          </div>

        {/* Floating {t('study.askGuruji')} */}
        <div style={{ position: 'fixed', bottom: '80px', right: '20px', zIndex: 500 }}>
          {gurujiOpen && (
            <div style={{ position: 'absolute', bottom: '60px', right: '0', width: '300px', background: 'white', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '65vh' }}>
              <div style={{ padding: '12px 16px', background: 'var(--brand-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>🎓</div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: 'white' }}>{t('study.askGuruji')}</div>
                    <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.6)' }}>{topic?.topic_name}</div>
                  </div>
                </div>
                <button onClick={() => setGurujiOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', width: '24px', height: '24px', borderRadius: '50%', cursor: 'pointer', fontSize: '12px' }}>✕</button>
              </div>
              <div style={{ flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', minHeight: '200px', maxHeight: '380px' }}>
                {gurujiMessages.map((msg, i) => (
                  <div key={i} style={{ maxWidth: '85%', alignSelf: msg.role === 'student' ? 'flex-end' : 'flex-start' }}>
                    <div style={{ fontSize: '10px', fontWeight: 800, marginBottom: '3px', color: msg.role === 'student' ? 'var(--brand-primary)' : 'var(--brand-secondary)' }}>
                      {msg.role === 'student' ? 'You' : '🎓 Guruji'}
                    </div>
                    <div style={{ padding: '8px 12px', fontSize: '12px', lineHeight: 1.5, background: msg.role === 'student' ? 'var(--brand-primary-light)' : 'var(--bg)', border: msg.role === 'student' ? 'none' : '1px solid var(--border)', borderRadius: msg.role === 'student' ? '12px 12px 0 12px' : '12px 12px 12px 0' }}>
                      {msg.text}
                      {msg.related_topic && (
                        <div onClick={() => { navigate(`/study/${msg.related_topic.id}`); setGurujiOpen(false); }} style={{ marginTop: '8px', padding: '6px 10px', background: 'var(--brand-secondary-light)', borderRadius: '8px', fontSize: '11px', color: 'var(--brand-secondary)', fontWeight: 600, cursor: 'pointer' }}>
                          <div style={{ fontSize: '9px', fontWeight: 800, marginBottom: '2px' }}>💡 Study This Next</div>
                          {msg.related_topic.name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {gurujiLoading && (
                  <div style={{ alignSelf: 'flex-start' }}>
                    <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-secondary)', marginBottom: '3px' }}>🎓 Guruji</div>
                    <div style={{ padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '12px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {[0,1,2].map(i => <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--muted)', animation: `bounce 1s ${i*0.2}s infinite` }}></div>)}
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px' }}>
                <input style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '20px', padding: '8px 14px', fontSize: '12px', outline: 'none' }} placeholder="Koi bhi sawaal poochho..." value={gurujiInput} onChange={e => setGurujiInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAskGuruji()} />
                <button onClick={handleAskGuruji} disabled={gurujiLoading} style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'var(--brand-primary)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▶</button>
              </div>
            </div>
          )}
          <button onClick={() => setGurujiOpen(!gurujiOpen)} style={{ width: '54px', height: '54px', borderRadius: '50%', background: gurujiOpen ? 'var(--brand-secondary)' : 'var(--brand-primary)', color: 'white', border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.25)', cursor: 'pointer', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {gurujiOpen ? '✕' : '🎓'}
          </button>
        </div>
      </div>

      <style>{`@keyframes bounce { 0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)} }`}</style>
    </AppShell>
  );
};

export default StudyPage;
